import {
  Blueprint,
  BlueprintExpense,
  BlueprintGovernance,
  BlueprintProduct,
  BlueprintResource,
} from "../../../pages/Projects/ServicesV2/types";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import DataTable from "@components/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import "./style.css";
import { getOrganizedPhasesFromListOfServices } from "@utils/helpers";
import { Phase } from "@utils/helpers";
import { useV2GetRateTableQuery, useV2GetPaymentTermQuery } from "@generated";

interface BlueprintItem {
  id: string;
  name?: string;
  quantity?: string;
  resource?: { id: string; name: string };
  phase?: Phase;
}

interface GeneralRow {
  id: string;
  name: string;
  value: string;
}

interface BlueprintPreviewProps {
  blueprint: Blueprint;
  slideoutOpen: boolean;
  setSlideoutOpen: (bool) => void;
  title?: string;
}

const BlueprintPreview = ({
  blueprint,
  slideoutOpen,
  setSlideoutOpen,
  title = "View Services In Blueprint",
}: BlueprintPreviewProps) => {
  const phases = getOrganizedPhasesFromListOfServices(
    blueprint?.services || []
  );

  const general = blueprint?.general;
  const rateTableId = general?.rateTableId;
  const paymentTermId = general?.paymentTermId;

  const { data: rateTableData } = useV2GetRateTableQuery(
    { id: Number(rateTableId) },
    { skip: !rateTableId }
  );
  const { data: paymentTermData } = useV2GetPaymentTermQuery(
    { id: Number(paymentTermId) },
    { skip: !paymentTermId }
  );

  const rateTableName = rateTableData?.data?.attributes?.name;
  const paymentTermName = paymentTermData?.data?.attributes?.name;

  const generalRows: GeneralRow[] = [];
  if (general?.executiveSummary)
    generalRows.push({
      id: "executive-summary",
      name: "Executive Summary",
      value: general.executiveSummary,
    });
  if (general?.solutionSummary)
    generalRows.push({
      id: "solution-summary",
      name: "Solution Summary",
      value: general.solutionSummary,
    });
  if (rateTableName)
    generalRows.push({
      id: "rate-table",
      name: "Rate Table",
      value: rateTableName,
    });
  if (paymentTermName)
    generalRows.push({
      id: "payment-term",
      name: "Payment Term",
      value: paymentTermName,
    });

  const columns: ColumnDef<BlueprintItem>[] = [
    {
      header: "Service Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Resource",
      cell(props) {
        return <div>{props.row.original?.resource?.name || ""}</div>;
      },
    },
  ];

  const generalColumns: ColumnDef<GeneralRow>[] = [
    {
      header: "Item Name",
      cell(props) {
        return props.row.original.name;
      },
    },
    {
      header: "Value",
      cell(props) {
        return <div className="generalValue">{props.row.original.value}</div>;
      },
    },
  ];

  const governanceColumns: ColumnDef<BlueprintGovernance>[] = [
    {
      header: "Description",
      cell(props) {
        return props.row.original.description || "";
      },
    },
    {
      header: () => <div className="alignRight">Hours</div>,
      id: "hours",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.hours || ""}</div>
        );
      },
    },
  ];

  const expenseColumns: ColumnDef<BlueprintExpense>[] = [
    {
      header: "Description",
      cell(props) {
        return props.row.original.description || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Rate",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.rate || ""}</div>
        );
      },
    },
  ];

  const resourceColumns: ColumnDef<BlueprintResource>[] = [
    {
      header: "Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: () => <div className="alignRight">Hours</div>,
      id: "hours",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.hours || ""}</div>
        );
      },
    },
    {
      header: () => <div className="alignRight">Rate</div>,
      id: "rate",
      cell(props) {
        return (
          <div className="alignRight">{props.row.original.rate || ""}</div>
        );
      },
    },
  ];

  const productColumns: ColumnDef<BlueprintProduct>[] = [
    {
      header: "Name",
      cell(props) {
        return props.row.original.name || "";
      },
    },
    {
      header: "Qty",
      cell(props) {
        return (
          <div className="alignCenter">{props.row.original.quantity || ""}</div>
        );
      },
    },
    {
      header: "Unit Price",
      cell(props) {
        return (
          <div className="alignCenter">
            {props.row.original.unitPrice || ""}
          </div>
        );
      },
    },
  ];

  const governances = blueprint?.governances || [];
  const expenses = blueprint?.expenses || [];
  const resources = blueprint?.resources || [];
  const products = blueprint?.products || [];

  const hasServiceSections = !!(phases?.length && blueprint?.services?.length);
  const hasAnyContent =
    generalRows.length > 0 ||
    hasServiceSections ||
    governances.length > 0 ||
    expenses.length > 0 ||
    resources.length > 0 ||
    products.length > 0;

  return (
    <SlideOutMenuV2
      title={title}
      isOpen={slideoutOpen}
      onClose={() => setSlideoutOpen(false)}
    >
      <div className="blueprintPreviewSlideout">
        {generalRows.length > 0 && (
          <div>
            <div className="previewSubheader">General</div>
            <DataTable
              data={generalRows}
              columns={generalColumns}
              bordered
              striped
            />
          </div>
        )}

        {hasServiceSections &&
          phases.map((p, i) => (
            <div key={i}>
              <div className="previewSubheader">{p?.name}</div>
              <DataTable
                data={
                  blueprint?.services?.filter(
                    (s) => s?.phase?.id === p?.id
                  ) || [{ id: "0", name: "No services found" }]
                }
                columns={columns}
                bordered
                striped
              />
            </div>
          ))}

        {governances.length > 0 && (
          <div>
            <div className="previewSubheader">Governance</div>
            <DataTable
              data={governances}
              columns={governanceColumns}
              bordered
              striped
            />
          </div>
        )}

        {expenses.length > 0 && (
          <div>
            <div className="previewSubheader">Expenses</div>
            <DataTable
              data={expenses}
              columns={expenseColumns}
              bordered
              striped
            />
          </div>
        )}

        {resources.length > 0 && (
          <div>
            <div className="previewSubheader">Resources</div>
            <DataTable
              data={resources}
              columns={resourceColumns}
              bordered
              striped
            />
          </div>
        )}

        {products.length > 0 && (
          <div>
            <div className="previewSubheader">Products</div>
            <DataTable
              data={products}
              columns={productColumns}
              bordered
              striped
            />
          </div>
        )}

        {!hasAnyContent && "No services found"}
      </div>
    </SlideOutMenuV2>
  );
};

export default BlueprintPreview;
